import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useBlogCategories, useBlogPosts } from '@/features/blog';
import { useCollections } from '@/features/collections';
import { usePages } from '@/features/pages';
import { usePolicies } from '@/features/policies';
import { useStoreProducts } from '@/features/store-catalog';

import { targetPath, type TargetKind, type TargetOption } from '../schemas/target-kinds';

import { SearchPickList } from './search-pick-list';

const PAGE_SIZE = 20;

interface ResultsProps {
  storeId: string;
  q: string;
  onQChange: (q: string) => void;
  search: string | undefined;
  label: string;
  selectedIds: ReadonlySet<string>;
  onPick: (option: TargetOption) => void;
}

function ProductResults({ storeId, search, ...rest }: ResultsProps) {
  const query = useStoreProducts(storeId, { limit: PAGE_SIZE, ...(search ? { q: search } : {}) });
  const items = useMemo(
    () =>
      query.data?.items.map((row) => ({
        id: row.id,
        label: row.title ?? row.product.title,
        detail: targetPath('product', row.handle),
      })),
    [query.data],
  );
  return (
    <SearchPickList
      {...rest}
      items={items}
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  );
}

function CollectionResults({ storeId, search, ...rest }: ResultsProps) {
  const query = useCollections(storeId, { limit: PAGE_SIZE, ...(search ? { q: search } : {}) });
  const items = useMemo(
    () =>
      query.data?.items.map((row) => ({
        id: row.id,
        label: row.title,
        detail: targetPath('collection', row.handle),
      })),
    [query.data],
  );
  return (
    <SearchPickList
      {...rest}
      items={items}
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  );
}

function PageResults({ storeId, search, ...rest }: ResultsProps) {
  const query = usePages(storeId, { limit: PAGE_SIZE, ...(search ? { q: search } : {}) });
  const items = useMemo(
    () =>
      query.data?.items.map((row) => ({
        id: row.id,
        label: row.title,
        detail: targetPath('page', row.handle),
      })),
    [query.data],
  );
  return (
    <SearchPickList
      {...rest}
      items={items}
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  );
}

// Policies are a bounded list without a search parameter, so the filter runs here.
function PolicyResults({ storeId, search, ...rest }: ResultsProps) {
  const query = usePolicies(storeId);
  const items = useMemo(() => {
    const needle = search?.toLowerCase();
    return query.data
      ?.filter(
        (row) => !needle || row.title.toLowerCase().includes(needle) || row.handle.includes(needle),
      )
      .map((row) => ({
        id: row.id,
        label: row.title,
        detail: targetPath('policy', row.handle),
      }));
  }, [query.data, search]);
  return (
    <SearchPickList
      {...rest}
      items={items}
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  );
}

function BlogPostResults({ storeId, search, ...rest }: ResultsProps) {
  const query = useBlogPosts(storeId, { limit: PAGE_SIZE, ...(search ? { q: search } : {}) });
  const items = useMemo(
    () =>
      query.data?.items.map((row) => ({
        id: row.id,
        label: row.title,
        detail: targetPath('blog_post', row.handle),
      })),
    [query.data],
  );
  return (
    <SearchPickList
      {...rest}
      items={items}
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  );
}

// Categories are a bounded list without a search parameter, so the filter runs here.
function BlogCategoryResults({ storeId, search, ...rest }: ResultsProps) {
  const query = useBlogCategories(storeId);
  const items = useMemo(() => {
    const needle = search?.toLowerCase();
    return query.data
      ?.filter(
        (row) => !needle || row.title.toLowerCase().includes(needle) || row.handle.includes(needle),
      )
      .map((row) => ({
        id: row.id,
        label: row.title,
        detail: targetPath('blog_category', row.handle),
      }));
  }, [query.data, search]);
  return (
    <SearchPickList
      {...rest}
      items={items}
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
    />
  );
}

const RESULTS: Record<TargetKind, (props: ResultsProps) => ReactNode> = {
  product: ProductResults,
  collection: CollectionResults,
  page: PageResults,
  policy: PolicyResults,
  blog_post: BlogPostResults,
  blog_category: BlogCategoryResults,
};

export interface TargetPickerProps {
  storeId: string;
  kind: TargetKind;
  selectedIds: ReadonlySet<string>;
  onPick: (option: TargetOption) => void;
}

/** Search one kind of store record and pick results; selection state belongs to the caller. */
export function TargetPicker({ storeId, kind, selectedIds, onPick }: TargetPickerProps) {
  const { t } = useTranslation('linkTargets');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState<string | undefined>(undefined);

  useEffect(() => {
    const trimmed = q.trim();
    const id = window.setTimeout(() => setSearch(trimmed || undefined), 250);
    return () => window.clearTimeout(id);
  }, [q]);

  const Results = RESULTS[kind];
  return (
    <Results
      storeId={storeId}
      q={q}
      onQChange={setQ}
      search={search}
      label={t(`results.${kind}`)}
      selectedIds={selectedIds}
      onPick={onPick}
    />
  );
}
